import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle } from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  ENTITLEMENT_PLATFORMS,
  fetchOrganizationCredits,
  grantCredits,
  type EntitlementPlatform,
  type LedgerRow,
} from '@/lib/adminCredits';
import { canGrantCredits } from '@/lib/roles';
import { useMe } from '@/lib/useMe';

const CREDITS_QUERY_KEY = (orgId: string) => ['admin-credits', orgId] as const;
/**
 * The floor a reason must clear WHEN ONE IS TYPED. The field itself is
 * OPTIONAL for a credit grant since 22.09.2026 (Rostyslav, owner-approved):
 * typing a sentence for every comp grant cost the support agents more time
 * than the sentence was worth.
 *
 * Optional is not "anything goes". Leave the box empty and the grant costs
 * nothing extra; type into it and it must still be a sentence, because a
 * half-typed "ok" sitting in an audit column is worse than an honest blank —
 * it looks like a record. Mirrors admin_credits.py's own rule; the API
 * refuses either way, this only keeps the button honest.
 *
 * NOT shared with ResetPasswordDialog's export of the same name: that one is
 * a floor on a MANDATORY field, and the day one of the two moves, a shared
 * constant would move the other silently.
 */
const MIN_REASON_LENGTH = 10;

function newIdempotencyKey(): string {
  // crypto.randomUUID is available in every browser this SPA targets (no IE
  // support needed) — one key per submit ATTEMPT, reused across a network
  // retry of that same attempt, per adminCredits.ts's grantCredits docstring.
  return crypto.randomUUID();
}

export function CreditsPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const { data: me } = useMe();
  const queryClient = useQueryClient();

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  // Which platform this currency is granted FOR (ClickUp 869f084w5 §3/§4) —
  // stamped on the ledger metadata AND the audit receipt, so the grants panel
  // can answer "for which platform" per row.
  const [platform, setPlatform] = useState<EntitlementPlatform>('WEB');
  const [confirming, setConfirming] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey());
  const [result, setResult] = useState<
    { ok: true; ledgerRowId: string | null; replayed: boolean } | { ok: false; message: string } | null
  >(null);

  // Shared with the "Grant credits" link on a user's card (lib/roles.ts) so
  // the two screens cannot drift into offering a viewer a control the API
  // would refuse.
  const canGrant = canGrantCredits(me?.role);

  const { data, isLoading, error } = useQuery({
    queryKey: CREDITS_QUERY_KEY(orgId ?? ''),
    queryFn: () => fetchOrganizationCredits(orgId!),
    enabled: !!orgId,
  });

  const mutation = useMutation({
    mutationFn: () =>
      grantCredits(
        orgId!,
        {
          amount: Number(amount),
          // `undefined`, not `''`: the API treats both as "no reason", but a
          // body a human reads should say the field was absent rather than
          // carrying an empty one.
          reason: reason.trim() || undefined,
          note: note.trim() || undefined,
          platform,
        },
        idempotencyKey
      ),
    onSuccess: (res) => {
      setResult({ ok: true, ledgerRowId: res.ledger_row_id, replayed: res.replayed });
      setAmount('');
      setReason('');
      setNote('');
      // A fresh key for the NEXT grant — reusing the old one here would make
      // a genuinely new grant replay as the previous one.
      setIdempotencyKey(newIdempotencyKey());
      void queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY(orgId ?? '') });
    },
    onError: (err: Error) => setResult({ ok: false, message: err.message }),
  });

  const amountNumber = Number(amount);
  const trimmedReason = reason.trim();
  const formValid =
    Number.isInteger(amountNumber) &&
    amountNumber > 0 &&
    // Empty is fine (the fast path). Non-empty must clear the floor.
    (trimmedReason.length === 0 || trimmedReason.length >= MIN_REASON_LENGTH);

  if (!orgId) return null;

  return (
    <div className="min-w-0 max-w-2xl [overflow-wrap:anywhere]">
      <h1 className="text-h2 font-semibold text-fg-primary">Credits — {orgId}</h1>
      <p className="mt-2 text-body">
        <Link className="text-fg-secondary underline" to={`/organizations/${orgId}/refund-calculator`}>
          Refund calculator
        </Link>{' '}
        <span className="text-fg-tertiary">— refund part of a purchase and take back the same share of its coins.</span>
      </p>

      {isLoading && <p className="mt-4 text-body text-fg-secondary">Loading…</p>}
      {error && (
        <p className="mt-4 text-body text-destructive-soft-foreground">
          {(error as Error).message}
        </p>
      )}

      {data && (
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-card border border-border bg-surface-field p-4">
            <div className="text-meta text-fg-secondary">Purchased wallet</div>
            <div className="mt-1 text-h3 font-semibold text-fg-primary">{data.balance}</div>
          </div>
          <div className="rounded-card border border-border bg-surface-field p-4">
            <div className="text-meta text-fg-secondary">Monthly limit</div>
            <div className="mt-1 text-h3 font-semibold text-fg-primary">
              {data.monthly_limit ?? 'unlimited'}
            </div>
            <div className="mt-1 text-micro text-fg-tertiary">
              window: {data.allowance_window.source} from {data.allowance_window.start}
            </div>
          </div>
        </div>
      )}

      {canGrant && (
        <form
          className="mt-6 flex flex-col gap-3 rounded-card border border-border bg-surface-field p-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (formValid) setConfirming(true);
          }}
        >
          <h2 className="text-h3 font-semibold text-fg-primary">Grant credits</h2>
          <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
            Amount
            <input
              type="number"
              min={1}
              step={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="min-w-0 rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
            />
          </label>
          <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
            Reason (optional — if you write one, at least {MIN_REASON_LENGTH} characters)
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="min-w-0 rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
            />
          </label>
          <label
            className="flex flex-col gap-1 text-meta font-medium text-fg-secondary"
            htmlFor="credits-platform"
          >
            Platform
            <select
              id="credits-platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value as EntitlementPlatform)}
              className="min-w-0 rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
            >
              {ENTITLEMENT_PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p === 'WEB' ? 'Web' : 'App'}
                </option>
              ))}
            </select>
            <span className="text-micro font-normal text-fg-tertiary">
              Labels this grant on the ledger and in the grants log. There is no App client yet —
              every account and every payment here is Web — so App records intent, it does not
              route credits anywhere else.
            </span>
          </label>
          <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
            Note (optional)
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="min-w-0 rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
            />
          </label>
          <button
            type="submit"
            disabled={!formValid || mutation.isPending}
            className="self-start rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Grant credits
          </button>
        </form>
      )}

      {result && (
        <div
          role="status"
          className={`mt-4 flex items-center gap-2 rounded-card border px-3 py-2 text-body ${
            result.ok
              ? 'border-success-border bg-success-soft text-success-soft-foreground'
              : 'border-destructive-border bg-destructive-soft text-destructive-soft-foreground'
          }`}
        >
          {result.ok ? (
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : (
            <XCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          {result.ok
            ? result.replayed
              ? `Already granted (idempotent replay) — ledger row ${result.ledgerRowId}`
              : `Granted — audit/ledger row ${result.ledgerRowId}`
            : result.message}
        </div>
      )}

      <h2 className="mt-8 text-h3 font-semibold text-fg-primary">Ledger (last 20)</h2>
      <div className="mt-2 overflow-x-auto rounded-card border border-border">
        <table className="w-full text-left text-body">
          <thead className="bg-surface-field text-meta text-fg-secondary">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2 text-right">Credits</th>
            </tr>
          </thead>
          <tbody>
            {(data?.ledger ?? []).map((row: LedgerRow) => (
              <tr key={row.id} className="border-t border-border">
                <td className="px-3 py-2 text-fg-secondary">{row.date_created ?? '—'}</td>
                <td className="px-3 py-2 text-fg-primary">{row.type}</td>
                <td className="px-3 py-2 text-fg-primary">{row.source}</td>
                <td className="px-3 py-2 text-right text-fg-primary">{row.credits}</td>
              </tr>
            ))}
            {data && data.ledger.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-fg-tertiary">
                  No ledger rows yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {confirming && (
        <ConfirmDialog
          title="Grant credits?"
          description={`Grant ${amount} credits to ${orgId} for ${platform}. ${
            trimmedReason ? `Reason: "${trimmedReason}".` : 'No reason given.'
          } This is written to the shared ledger and audited.`}
          confirmLabel="Grant credits"
          busy={mutation.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            mutation.mutate();
            setConfirming(false);
          }}
        />
      )}
    </div>
  );
}
